import { Chip } from '@mui/material';
import { Box } from '@mui/material';
import { Link } from '@mui/material';
import { useNavigation } from '@refinedev/core';
import { EditButton, List, ShowButton } from '@refinedev/mui';
import { useDataGrid } from '@refinedev/mui';
import { DataGrid, type GridColDef } from '../../../components/DataGrid.tsx';
import { useColumnVisibilityModel } from '../../../hooks/columnVisibilityModel.ts';
import { OrganizationWithAccountsAndMagazines, type Organization } from '../../../types/account.ts';

export const getStatusLabel = (organization: Organization) => {
  switch (organization.status) {
    case 'active':
      if (organization.validFrom < new Date()) {
        return '利用中';
      }
      return '利用開始前';
    case 'inactive':
      return '停止中';
    default:
      return organization.status;
  }
};

export const OrganizationList = () => {
  const { push } = useNavigation();
  const { dataGridProps, filters, setFilters } = useDataGrid<OrganizationWithAccountsAndMagazines>({
    resource: 'accounts/organizations',
    queryOptions: {
      select({ data, total }) {
        return {
          data: data.map((row) => OrganizationWithAccountsAndMagazines.parse(row)),
          total,
        };
      },
    },
  });

  const columns: GridColDef<OrganizationWithAccountsAndMagazines>[] = [
    {
      type: 'actions',
      field: 'actions',
      headerName: '',
      maxWidth: 120,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: function render({ row }) {
        return (
          <>
            <EditButton hideText recordItemId={row.id} />
            <ShowButton hideText recordItemId={row.id} />
          </>
        );
      },
    },
    {
      field: 'id',
      headerName: '組織ID',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'name',
      headerName: '組織名',
      minWidth: 300,
      flex: 1,
    },
    {
      field: 'accounts',
      headerName: '利用者',
      filterable: false,
      sortable: false,
      minWidth: 150,
      renderCell: function render({ row }) {
        return (
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              push(`/organization/${row.id}/accounts`);
            }}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {row.accounts.length}/{row.accountLimitNum}
          </Link>
        );
      },
    },
    {
      field: 'magazines',
      headerName: '雑誌',
      filterable: false,
      sortable: false,
      minWidth: 150,
      renderCell: function render({ row }) {
        return (
          <Box
            sx={{
              lineHeight: 1.7,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <div>
              {row.magazines.nbl ? (
                <>
                  {row.magazines.nbl.name}: {row.magazines.nbl.accountCount ?? '-'} /{' '}
                  {row.magazines.nbl.accountLimitNum}
                  <br />
                </>
              ) : null}
              {row.magazines.junkan ? (
                <>
                  {row.magazines.junkan.name}: {row.magazines.junkan.accountCount ?? '-'} /{' '}
                  {row.magazines.junkan.accountLimitNum}
                  <br />
                </>
              ) : null}
              {row.magazines.shiryoban ? (
                <>
                  {row.magazines.shiryoban.name}: {row.magazines.shiryoban.accountCount ?? '-'} /{' '}
                  {row.magazines.shiryoban?.accountLimitNum}
                  <br />
                </>
              ) : null}
            </div>
          </Box>
        );
      },
    },
    {
      type: 'date',
      field: 'validFrom',
      headerName: '利用開始日',
      minWidth: 200,
      valueFormatter: (_, { validFrom }) => validFrom.toLocaleDateString('ja-JP'),
    },
    {
      field: 'organizationType',
      headerName: '組織タイプ',
      filterable: true,
      sortable: false,
      minWidth: 200,
      valueGetter(_, row) {
        return row.organizationType?.name;
      },
    },
    {
      field: 'seller',
      headerName: '販売経路',
      filterable: true,
      sortable: false,
      minWidth: 200,
      valueGetter(_, row) {
        return row.seller?.name;
      },
    },
    {
      type: 'singleSelect',
      field: 'status',
      headerName: 'ステータス',
      filterable: true,
      sortable: false,
      valueOptions: ['active', 'inactive'],
      getOptionLabel(key) {
        if (typeof key !== 'string') return '';
        return (
          {
            active: '利用中 / 利用開始前',
            inactive: '停止中',
          }[key] ?? ''
        );
      },
      minWidth: 200,
      renderCell: function render({ row }) {
        return (
          <Chip
            label={getStatusLabel(row)}
            color={row.status === 'active' ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      type: 'dateTime',
      field: 'createdAt',
      headerName: '作成日時',
      display: 'flex',
      minWidth: 150,
      valueFormatter(_, { createdAt }) {
        if (!createdAt) return '-';
        return createdAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
      },
    },
    {
      field: 'createdBy',
      headerName: '作成者',
      display: 'flex',
      minWidth: 150,
      valueGetter(_, row) {
        return row.createdBy ?? '-';
      },
    },
    {
      type: 'dateTime',
      field: 'modifiedAt',
      headerName: '変更日時',
      display: 'flex',
      minWidth: 150,
      valueFormatter(_, { modifiedAt }) {
        if (!modifiedAt) return '-';
        return modifiedAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
      },
    },
    {
      field: 'modifiedBy',
      headerName: '変更者',
      display: 'flex',
      minWidth: 150,
      valueGetter(_, row) {
        return row.modifiedBy ?? '-';
      },
    },
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useColumnVisibilityModel('accounts/organizations');

  return (
    <List>
      <DataGrid
        {...dataGridProps}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        filterDebounceMs={800}
        defaultFilterColumn="name"
        rowHeight={100}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />
    </List>
  );
};
